import type { CSSProperties } from 'react';
import {
  createColumnHelper,
  metaHelper,
  tableFeatures,
  useTable,
  type Row,
} from '@tanstack/react-table';

import { HiddenLabelsCount, NarrowingLabelChip } from '@/features/labels';
import type { TaskListDensity } from '@/shared/store/preferences';
import { TaskCostFigure, type WorkspaceTaskRow } from '@/features/tasks';
import { displayNameOf } from '@/shared/components/accountIdentity';
import {
  formatExactTime,
  formatRelativeTime,
  formatSpan,
} from '@/shared/utils/time';

import { StateCell, TaskCell } from './components/TaskCells';
import type { SortKey } from './listView';
import { leadMs } from './taskSearch';
import { isStale } from './taskTiles';

/**
 * The Tasks page's columns, defined once (spec 110, research R5).
 *
 * Each column carries its heading, its cell and — in `meta` — the grid track it
 * occupies and the width below which it steps aside. The heading row and every
 * row build their layout from this one list, so a column cannot be added to one
 * and forgotten in the other, and the next slice's sortable headings and column
 * choice are configuration on these definitions rather than a second layout.
 *
 * The table owns **no state**. Rows arrive already narrowed by
 * `matchesListView` and ordered by `orderTaskRows`; nothing here filters or
 * sorts, and when sorting arrives it will be controlled from the address like
 * every other narrowing, never held inside the table.
 */

type Breakpoint = 'sm' | 'md' | 'lg';

export interface TaskColumnMeta {
  /** This column's `grid-template-columns` track. */
  track: string;
  /** Hidden below this breakpoint. Absent: always shown. */
  hideBelow?: Breakpoint;
  /** Figures read right-aligned, so they line up down the column. */
  align?: 'end';
  /**
   * The column's name, read out before the value. The heading row is visual
   * only — every row is a single link, which a table row cannot also be — so a
   * value whose meaning depends on its column says which column it is in.
   */
  srLabel?: string;
  /** Pressing the heading orders the list by this (spec 112 US3). */
  sort?: SortKey;
}

export interface TaskTableMeta {
  workspaceId: string;
  /** Toggle a label in the page's own narrowing. */
  onToggleLabel: (labelId: string) => void;
  /** Each workflow's usual lead time, for marking stuck tasks (spec 113 FR-008). */
  leadMedians: ReadonlyMap<string, number>;
  /** Two lines or one (spec 114 US2). */
  density: TaskListDensity;
}

export const taskTableFeatures = tableFeatures({
  columnMeta: metaHelper<TaskColumnMeta>(),
  tableMeta: metaHelper<TaskTableMeta>(),
});

export type TaskTableRow = Row<typeof taskTableFeatures, WorkspaceTaskRow>;

const column = createColumnHelper<typeof taskTableFeatures, WorkspaceTaskRow>();

function nameOf(person: WorkspaceTaskRow['assignee']): string | null {
  return person ? displayNameOf(person.display_name, person.email) : null;
}

export const taskColumns = column.columns([
  column.accessor('title', {
    id: 'task',
    header: 'Task',
    meta: { track: 'minmax(0,1fr)', sort: 'task' },
    cell: ({ row: { original: row }, table }) => (
      <TaskCell row={row} density={table.options.meta?.density} />
    ),
  }),
  column.accessor('state', {
    id: 'state',
    header: 'State',
    meta: { track: '8.5rem', sort: 'state' },
    cell: ({ row: { original: row }, table }) => (
      <StateCell row={row} density={table.options.meta?.density} />
    ),
  }),
  column.display({
    id: 'labels',
    header: 'Labels',
    meta: { track: 'minmax(0,8rem)', hideBelow: 'md' },
    cell: ({ row: { original: row }, table }) => {
      // One line in a narrow column: the first label, then a count of the
      // rest, as a board card does. Laying them all out clipped the second
      // chip through the middle, or hid it with nothing to say so.
      const [first, ...rest] = row.labels;
      if (!first) return null;
      return (
        <span className="flex min-w-0 items-center gap-1">
          {/*
            `stopPropagation`, because this chip sits inside the row link and
            must swallow the click that would otherwise navigate — the exact
            opposite of a chip on a board card, and both rules are tested.
          */}
          <NarrowingLabelChip
            label={first}
            stopPropagation
            truncate
            onNarrow={(labelId) => {
              table.options.meta?.onToggleLabel(labelId);
            }}
          />
          <HiddenLabelsCount labels={rest} className="shrink-0" />
        </span>
      );
    },
  }),
  column.accessor((row) => nameOf(row.assignee) ?? '', {
    id: 'assignee',
    header: 'Assignee',
    meta: {
      track: '7rem',
      hideBelow: 'lg',
      srLabel: 'Assigned to',
      sort: 'assignee',
    },
    cell: ({ row: { original: row } }) => {
      const name = nameOf(row.assignee);
      return name ? (
        <span className="text-foreground block truncate" title={name}>
          {name}
        </span>
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    },
  }),
  column.accessor('createdAt', {
    id: 'created',
    header: 'Created',
    meta: {
      track: '4.5rem',
      hideBelow: 'lg',
      align: 'end',
      srLabel: 'Created',
      sort: 'created',
    },
    cell: ({ row: { original: row } }) => {
      const by = nameOf(row.createdBy);
      return (
        <span
          title={`Created ${formatExactTime(row.createdAt)}${by ? ` by ${by}` : ''}`}
        >
          {formatRelativeTime(row.createdAt)}
        </span>
      );
    },
  }),
  column.accessor((row) => leadMs(row, Date.now()), {
    id: 'leadTime',
    header: 'Lead time',
    meta: {
      track: '5.5rem',
      hideBelow: 'sm',
      align: 'end',
      srLabel: 'Lead time',
      sort: 'leadTime',
    },
    cell: ({ row: { original: row }, table }) => {
      const now = Date.now();
      const medians = table.options.meta?.leadMedians;
      // Well past what this workflow's tasks usually take — a mark for the
      // reader only; it changes no state, filter, order or tile (FR-008).
      const usual = medians?.get(row.workflowKey);
      const stale =
        medians !== undefined &&
        usual !== undefined &&
        isStale(row, medians, now);
      return (
        <span
          className={`whitespace-nowrap ${stale ? 'text-warning font-medium' : ''}`}
          data-stale={stale ? 'true' : undefined}
          title={
            stale && usual !== undefined
              ? `More than twice the usual ${formatSpan(usual)} for ${row.workflowName}`
              : undefined
          }
        >
          {formatSpan(leadMs(row, now))}
          {row.finishedAt === null ? (
            <span className="text-muted-foreground/70"> so far</span>
          ) : null}
        </span>
      );
    },
  }),
  column.display({
    id: 'cost',
    header: 'Cost',
    meta: {
      track: '5.5rem',
      hideBelow: 'sm',
      align: 'end',
      srLabel: 'Cost',
      sort: 'cost',
    },
    cell: ({ row: { original: row } }) => (
      <TaskCostFigure cost={row.cost} placeholder="—" />
    ),
  }),
  column.accessor('updatedAt', {
    id: 'updated',
    header: 'Updated',
    meta: {
      track: '4.5rem',
      align: 'end',
      srLabel: 'Updated',
      sort: 'updated',
    },
    cell: ({ row: { original: row } }) => (
      /*
        Titled, because "2h ago" alone does not say *of what*. The list is
        ordered by this value, so without a name nothing on the page would state
        what it is ordered by.
      */
      <span title={`Last changed ${formatExactTime(row.updatedAt)}`}>
        {formatRelativeTime(row.updatedAt)}
      </span>
    ),
  }),
]);

/** The table over already-narrowed, already-ordered rows. It owns no state. */
export function useTaskTable(
  rows: readonly WorkspaceTaskRow[],
  meta: TaskTableMeta,
) {
  return useTable({
    features: taskTableFeatures,
    columns: taskColumns,
    data: rows,
    getRowId: (row) => row.taskId,
    meta,
  });
}

/* ---------------------------------------------------------------------------
   Layout: one template, built from the columns, per breakpoint.

   A column hidden with `display: none` still owns its grid track, so hiding a
   cell is not enough — the template itself has to drop the track below the
   same breakpoint. The four templates are computed here and handed to CSS as
   custom properties; the class that reads them is a fixed string, which is
   what lets Tailwind see it.
   --------------------------------------------------------------------------- */

const BREAKPOINTS: readonly Breakpoint[] = ['sm', 'md', 'lg'];

/** Hidden below `hideBelow`: at these widths the column is not shown. */
function hiddenAt(meta: TaskColumnMeta, width: 'base' | Breakpoint): boolean {
  if (!meta.hideBelow) return false;
  if (width === 'base') return true;
  return BREAKPOINTS.indexOf(width) < BREAKPOINTS.indexOf(meta.hideBelow);
}

function template(width: 'base' | Breakpoint): string {
  return taskColumns
    .map((c) => c.meta)
    .filter((meta): meta is TaskColumnMeta => meta !== undefined)
    .filter((meta) => !hiddenAt(meta, width))
    .map((meta) => meta.track)
    .join(' ');
}

export const TASK_GRID_STYLE = {
  '--task-cols-base': template('base'),
  '--task-cols-sm': template('sm'),
  '--task-cols-md': template('md'),
  '--task-cols-lg': template('lg'),
} as CSSProperties;

export const TASK_GRID_CLASS =
  'grid gap-x-3 px-3 [grid-template-columns:var(--task-cols-base)] sm:[grid-template-columns:var(--task-cols-sm)] md:[grid-template-columns:var(--task-cols-md)] lg:[grid-template-columns:var(--task-cols-lg)]';

const HIDE_CLASS: Record<Breakpoint, string> = {
  sm: 'max-sm:hidden',
  md: 'max-md:hidden',
  lg: 'max-lg:hidden',
};

/** The wrapper classes a cell (or its heading) takes from its column. */
export function cellClass(meta: TaskColumnMeta | undefined): string {
  return [
    'min-w-0',
    meta?.hideBelow ? HIDE_CLASS[meta.hideBelow] : '',
    meta?.align === 'end' ? 'text-right' : '',
  ]
    .filter(Boolean)
    .join(' ');
}
